import React, { useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import Products2Hero from '../components/Products2Hero';
import { 
  TrendingUp, 
  Truck, 
  HeadphonesIcon, 
  MessagesSquare, 
  CircleDollarSign, 
  CheckCircle, 
  Briefcase, 
  GraduationCap, 
  MonitorSmartphone, 
  Handshake,
  ArrowRight,
  ShieldCheck
} from 'lucide-react';
import './BusinessPage.css';

const BusinessPage = () => {
  const sectionsRef = useRef([]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.remove('opacity-0', 'translate-y-8');
            entry.target.classList.add('opacity-100', 'translate-y-0');
          }
        });
      },
      { threshold: 0.1 }
    );
    
    sectionsRef.current.forEach((el) => {
      if (el) observer.observe(el);
    });
    
    return () => observer.disconnect();
  }, []);

  const addToRefs = (el) => {
    if (el && !sectionsRef.current.includes(el)) {
      sectionsRef.current.push(el);
    }
  };

  return (
    <div className="business-page bg-[#f8f9fa] text-[#1A1A1A] font-[Inter]">
      <Helmet>
        <title>Wholesale Refurbished Laptops | Best Dealer Prices | Nexawave Business</title>
        <meta name="description" content="Buy in bulk and save. Nexawave provides the best wholesale prices on refurbished laptops for dealers and businesses. Certified quality, certified savings." />
        <meta name="keywords" content="wholesale laptops, buy bulk laptops, laptop dealer hyderabad, refurbished laptops business, reseller laptop prices" />
      </Helmet>
      <Products2Hero 
        className="hero-tight"
        tagline="ENTERPRISE SOLUTIONS · BULK DISCOUNTS · PROFESSIONAL SUPPORT"
        title1="Business Solutions"
        title2=""
        title3=""
        description=""
        button1Text="Become a Dealer"
        button1Link="mailto:business@nexawave.com"
        button2Text="Call Sales"
        button2Link="tel:8688090217"
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-16 space-y-16 relative z-10">
        
        {/* Bulk Buying Section */}
        <div ref={addToRefs} className="transition-all duration-1000 opacity-0 translate-y-8">
          <div className="flex flex-col sm:flex-row sm:items-end justify-between mb-12 gap-4">
            <div className="max-w-2xl">
              <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-[#0063B8]/30 bg-[#0063B8]/10 text-[#0063B8] text-xs font-bold uppercase tracking-widest mb-4 block w-fit">
                WHY PARTNER WITH US
              </span>
              <h2 className="text-4xl sm:text-5xl font-extrabold tracking-tight leading-[1.05]" style={{ color: '#0D1F17' }}>
                  Wholesale <span className="text-[#0063B8]">Excellence.</span>
              </h2>
              <p className="mt-4 sm:mt-6 text-gray-600 text-base sm:text-lg leading-relaxed">
                We empower laptop dealers, resellers, and bulk purchasers with unparalleled pricing, rigorous quality control, and massive inventory scaling.
              </p>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 sm:gap-6">
            {[
              { icon: TrendingUp, title: 'Unbeatable Dealer Margins', desc: 'Unlock extremely aggressive, tiered wholesale pricing on refurbished units. The more you buy, the higher your retail margins soar.', gradient: 'from-blue-600 to-cyan-500' },
              { icon: ShieldCheck, title: 'Stringent Certification', desc: 'Every laptop endures a rigorous multi-point inspection. We guarantee pristine working condition to ensure zero complaints from your end-customers.', gradient: 'from-[#0063B8] to-blue-500' },
              { icon: Truck, title: 'Reliable Bulk Logistics', desc: 'Rapid, secure palletized shipping directly to your showroom or warehouse. We ensure your shelves remain fully stocked without delay.', gradient: 'from-cyan-500 to-blue-400' }
            ].map((item, i) => {
              const Icon = item.icon;
              return (
                <div key={i} className="group relative p-6 sm:p-8 bg-white border border-gray-100 rounded-[1.5rem] shadow-sm hover:shadow-xl transition-all duration-500 hover:-translate-y-1 overflow-hidden flex flex-col items-start">
                  <div className={`absolute top-0 right-0 w-24 h-24 sm:w-32 sm:h-32 bg-gradient-to-br ${item.gradient} opacity-[0.03] rounded-bl-[100px] transition-all duration-500 group-hover:scale-150 group-hover:opacity-[0.08]`}></div>
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-6 bg-gradient-to-br ${item.gradient} text-white shadow-md transform group-hover:scale-110 transition-transform duration-500`}>
                    <Icon size={24} strokeWidth={1.5} />
                  </div>
                  <h3 className="text-xl sm:text-2xl font-extrabold mb-2 sm:mb-3 text-[#0D1F17] group-hover:text-[#0063B8] transition-colors">{item.title}</h3>
                  <p className="text-gray-500 text-sm sm:text-base leading-relaxed">{item.desc}</p>
                </div>
              );
            })}
          </div>
        </div>

        {/* How It Works Section */}
        <div ref={addToRefs} className="transition-all duration-1000 delay-100 opacity-0 translate-y-8">
          <div className="flex flex-col sm:flex-row sm:items-end justify-between mb-12 gap-4">
            <div className="max-w-2xl">
              <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-[#0063B8]/30 bg-[#0063B8]/10 text-[#0063B8] text-xs font-bold uppercase tracking-widest mb-4 block w-fit">
                SOURCING PROCESS
              </span>
              <h2 className="text-4xl sm:text-5xl font-extrabold tracking-tight leading-[1.05]" style={{ color: '#0D1F17' }}>
                  Streamlined <span className="text-[#0063B8]">Fulfillment.</span>
              </h2>
              <p className="mt-4 sm:mt-6 text-gray-600 text-base sm:text-lg leading-relaxed">
                Procuring high-quality refurbished stock has never been easier. Four simple steps to load your inventory pipeline.
              </p>
            </div>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 sm:gap-6 relative">
            {/* Connecting Line (Desktop only) */}
            <div className="hidden lg:block absolute top-[3.5rem] left-[10%] right-[10%] h-[2px] bg-gradient-to-r from-[#0063B8]/10 via-[#0063B8]/30 to-[#0063B8]/10 z-0"></div>
            
            {[
              { num: '01', icon: MessagesSquare, title: 'Share Specs', desc: 'Contact our B2B team with your specific hardware requirements and volume target.' },
              { num: '02', icon: CircleDollarSign, title: 'Dealer Quote', desc: 'Receive exclusive access to our wholesale catalog and dealer pricing matrix.' },
              { num: '03', icon: CheckCircle, title: 'Lock Inventory', desc: 'Finalize your selection and secure your bulk order of certified refurbished laptops.' },
              { num: '04', icon: Truck, title: 'Pallet Delivery', desc: 'We securely pack and dispatch your order. Track your shipment straight to your facility.' }
            ].map((step, i) => {
              const Icon = step.icon;
              return (
                <div key={i} className="relative z-10 flex flex-col items-center text-center group">
                  <div className="w-12 h-12 sm:w-14 sm:h-14 bg-white border-2 border-[#0063B8]/20 text-[#0063B8] rounded-full flex items-center justify-center font-bold text-lg mb-4 shadow-sm group-hover:bg-[#0063B8] group-hover:text-white group-hover:border-[#0063B8] transition-all duration-300">
                    {step.num}
                  </div>
                  <div className="bg-white p-6 sm:p-8 rounded-[1.5rem] border border-gray-100 shadow-sm w-full h-full group-hover:shadow-md transition-all duration-300 overflow-hidden relative">
                    <Icon className="absolute -right-4 -bottom-4 w-20 h-20 sm:w-24 sm:h-24 text-[#0063B8] opacity-[0.03] group-hover:opacity-10 transition-opacity duration-300" strokeWidth={1} />
                    <h3 className="text-lg sm:text-xl font-extrabold mb-2 text-[#0D1F17]">{step.title}</h3>
                    <p className="text-gray-500 text-sm leading-relaxed">{step.desc}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Business Solutions Section */}
        <div ref={addToRefs} className="transition-all duration-1000 delay-200 opacity-0 translate-y-8">
          <div className="flex flex-col sm:flex-row sm:items-end justify-between mb-12 gap-4">
            <div className="max-w-2xl">
              <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-[#0063B8]/30 bg-[#0063B8]/10 text-[#0063B8] text-xs font-bold uppercase tracking-widest mb-4 block w-fit">
                TARGET MARKETS
              </span>
              <h2 className="text-4xl sm:text-5xl font-extrabold tracking-tight leading-[1.05]" style={{ color: '#0D1F17' }}>
                  Tailored <span className="text-[#0063B8]">Reseller Programs.</span>
              </h2>
              <p className="mt-4 sm:mt-6 text-gray-600 text-base sm:text-lg leading-relaxed">
                Whether you sell to local businesses, students, or corporations, we have a wholesale model built for your success.
              </p>
            </div>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 sm:gap-6">
            {[
              { icon: Briefcase, title: 'Local Dealerships', desc: 'Secure reliable stock for your local computer store with our specialized retail-ready refurbished units.' },
              { icon: GraduationCap, title: 'Institutional Suppliers', desc: 'Fulfill massive campus and school district tech tenders by leveraging our deep inventory of durable student laptops.' },
              { icon: MonitorSmartphone, title: 'Corporate Refurbishing', desc: 'Equip fast-growing offices entirely on a refurbished budget. High-spec business machines at a fraction of the cost.' },
              { icon: Handshake, title: 'Certified Reseller Network', desc: 'Join our elite tier of resellers to unlock our lowest possible unit prices, priority allocations, and deep market insights.' }
            ].map((sol, i) => {
              const Icon = sol.icon;
              return (
                <div key={i} className="group flex flex-col sm:flex-row items-start sm:items-center p-6 sm:p-8 bg-white border border-gray-100 rounded-[1.5rem] shadow-sm hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                  <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-xl bg-[#0063B8]/5 text-[#0063B8] flex items-center justify-center shrink-0 mb-4 sm:mb-0 sm:mr-6 group-hover:bg-[#0063B8] group-hover:text-white transition-all duration-300">
                    <Icon size={24} strokeWidth={1.5} />
                  </div>
                  <div>
                    <h3 className="text-xl sm:text-2xl font-extrabold mb-2 text-[#0D1F17] group-hover:text-[#0063B8] transition-colors">{sol.title}</h3>
                    <p className="text-gray-500 text-sm sm:text-base leading-relaxed">{sol.desc}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Premium Contact Banner */}
        <div ref={addToRefs} className="transition-all duration-1000 delay-300 opacity-0 translate-y-8">
          <div className="relative overflow-hidden rounded-[2rem] sm:rounded-[2.5rem] bg-[#0063B8] text-white p-8 sm:p-12 lg:p-16 shadow-xl text-center isolation-auto border border-white/10">
            {/* Abstract Background Elements */}
            <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full blur-3xl mix-blend-overlay"></div>
            <div className="absolute bottom-0 left-0 w-64 h-64 bg-black/20 rounded-full blur-3xl mix-blend-overlay"></div>
            
            <div className="relative z-10 max-w-3xl mx-auto">
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold mb-4 sm:mb-6 tracking-tight text-white drop-shadow-sm">
                Ready to Stock Your Store?
              </h2>
              <p className="text-blue-100 text-base sm:text-lg md:text-xl font-medium mb-8 sm:mb-10 max-w-2xl mx-auto leading-relaxed">
                Speak directly with our wholesale hardware specialists to secure the absolute highest margins on bulk refurbished laptop orders.
              </p>
              
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4">
                <a href="tel:8688090217" className="w-full sm:w-auto px-6 py-3 sm:px-8 sm:py-3.5 bg-white text-[#0063B8] font-bold rounded-lg shadow-lg hover:shadow-xl hover:scale-[1.02] active:scale-[0.98] transition-all duration-300 flex items-center justify-center gap-2 text-sm sm:text-base">
                  <HeadphonesIcon size={18} />
                  Call: 8688090217
                </a>
                <a href="mailto:business@nexawave.com" className="w-full sm:w-auto px-6 py-3 sm:px-8 sm:py-3.5 bg-transparent border border-white/40 text-white font-bold rounded-lg hover:bg-white/10 active:scale-[0.98] transition-all duration-300 flex items-center justify-center gap-2 text-sm sm:text-base">
                  Email Wholesale Team
                  <ArrowRight size={18} />
                </a>
              </div>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
};

export default BusinessPage;
